import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { generateSalaryPageUrl, extractLocationContext, canLinkToSalary, createOccupationSlug } from '@/lib/occupationUtils';
import { isCrawlableSalaryLink } from '@/lib/seoIndexing';
import { cn } from '@/lib/utils';

interface OccupationLinkProps {
  occupation: {
    title?: string;
    occ_title?: string;
    soc?: string;
    soc_code?: string;
    slug?: string;
  };
  location?: string;
  className?: string;
  children?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}

/**
 * Renders an occupation link as a crawlable `<a>` for indexed pages (good for
 * SEO) or as a nofollow `<a>` for non-indexed pages (preserves UX without
 * wasting crawl budget).
 */
const OccupationLink: React.FC<OccupationLinkProps> = ({
  occupation,
  location,
  className,
  children,
  onClick,
  disabled = false
}) => {
  const currentLocation = useLocation();
  
  const targetLocation = location || extractLocationContext(currentLocation.pathname);
  const title = occupation.title || occupation.occ_title || '';
  const canLink = canLinkToSalary(occupation) && !disabled;
  
  if (!canLink) {
    return (
      <span className={cn('text-foreground', className)}>
        {children || title}
      </span>
    );
  }
  
  const url = generateSalaryPageUrl(occupation, targetLocation);
  const occSlug = occupation.slug || createOccupationSlug(title);
  const locationSlug = targetLocation.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  const crawlable = isCrawlableSalaryLink(occSlug, locationSlug);

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      onClick();
    }
    // Let the <Link> handle navigation naturally
  };
  
  return (
    <Link
      to={url}
      onClick={handleClick}
      {...(!crawlable && { rel: 'nofollow' })}
      className={cn(
        'text-left underline-offset-4 hover:underline',
        'text-primary hover:text-primary/80',
        'cursor-pointer transition-colors',
        'whitespace-normal break-words',
        className
      )}
      title={`View salary details for ${title}`}
      aria-label={`View salary details for ${title}`}
    >
      {children || title}
    </Link>
  );
};

export default OccupationLink;
